import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    TextField,
    Grid,
    Card,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    InputAdornment,
} from '@mui/material';
import {
    Check as CheckIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    Facebook as FacebookIcon,
    LinkedIn as LinkedInIcon,
    YouTube as YouTubeIcon,
    Link as LinkIcon,
    Language as LanguageIcon,
    Share as ShareIcon,
    OpenInNew as OpenInNewIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import mediaService from '../services/mediaService';
import { useToast } from '../contexts/ToastContext';

const MediaManagement = () => {
    const { showToast } = useToast();

    // State management
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination and filtering
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Verification dialog state
    const [verificationDialog, setVerificationDialog] = useState({
        open: false,
        type: '', // 'verify' or 'reject'
        mediaId: null,
        reason: ''
    });

    // Image modal state
    const [imageModal, setImageModal] = useState({
        open: false,
        imageUrl: '',
        title: ''
    });

    const fetchMediaPersonnel = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page,
                limit: rowsPerPage,
                status: statusFilter,
                search: search.trim()
            };

            const response = await mediaService.getAllMediaPersonnel(params);

            if (response && response.success) {
                setMediaList(response.data || []);
                if (response.pagination) {
                    setTotalPages(response.pagination.totalPages);
                }
            } else {
                setError('Invalid response from server');
            }
        } catch (error) {
            setError(error.message || 'Error fetching media personnel');
            showToast(error.message || 'Error fetching media personnel', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMediaPersonnel();
    }, [page, rowsPerPage, statusFilter, search]);

    const handleViewDetails = (media) => {
        setSelectedMedia(media);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedMedia(null);
    };

    const handleVerify = (media) => {
        setVerificationDialog({
            open: true,
            type: 'verify',
            mediaId: media._id,
            reason: ''
        });
    };

    const handleReject = (media) => {
        setVerificationDialog({
            open: true,
            type: 'reject',
            mediaId: media._id,
            reason: ''
        });
    };

    const handleVerificationSubmit = async () => {
        try {
            setActionLoading(true);

            if (verificationDialog.type === 'verify') {
                await mediaService.verifyMediaPersonnel(verificationDialog.mediaId);
                showToast('Media personnel verified successfully!', 'success');
            } else {
                await mediaService.rejectMediaPersonnel(
                    verificationDialog.mediaId,
                    verificationDialog.reason
                );
                showToast('Media personnel rejected successfully!', 'success');
            }

            setVerificationDialog({ open: false, type: '', mediaId: null, reason: '' });
            fetchMediaPersonnel();
            if (selectedMedia && selectedMedia._id === verificationDialog.mediaId) {
                setOpenDialog(false); // Close details dialog if open
            }
        } catch (error) {
            showToast(error.message || 'Error processing request', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'verified': return 'success';
            case 'rejected': return 'error';
            default: return 'warning';
        }
    };

    // Helper to construct image URL
    const getImageUrl = (path) => {
        if (!path) return '';
        // Assuming backend serves public assets correctly
        // Adjust base URL if needed, similar to kycService
        const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";
        // Path stored in DB is like "public/asset/media/..."
        // We need to convert it to a URL. 
        // Assuming "public" is the static root.
        return `${API_BASE_URL}/${path.replace('public/', '')}`;
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                    Media Management
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchMediaPersonnel}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name, email, org..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="verified">Verified</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : mediaList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography>No media personnel found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                mediaList.map((media) => (
                                    <TableRow key={media._id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {media.name} {media.surname}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {media.mediaOrganizationName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {media.mediaOrganizationType}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{media.role}</TableCell>
                                        <TableCell>{media.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={media.status}
                                                color={getStatusColor(media.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(media.createdAt).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleViewDetails(media)}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                            {media.status === 'pending' && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => handleVerify(media)}
                                                    >
                                                        <CheckIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleReject(media)}
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalPages > 1 && (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}
            </Paper>

            {/* Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                {selectedMedia && (
                    <>
                        <DialogTitle>
                            Media Personnel Details
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Personal Info</Typography>
                                    <Typography variant="body2"><strong>Name:</strong> {selectedMedia.name} {selectedMedia.surname}</Typography>
                                    <Typography variant="body2"><strong>Email:</strong> {selectedMedia.email}</Typography>
                                    <Typography variant="body2"><strong>Mobile:</strong> {selectedMedia.mobileNumber}</Typography>

                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>Organization Info</Typography>
                                    <Typography variant="body2"><strong>Organization:</strong> {selectedMedia.mediaOrganizationName}</Typography>
                                    <Typography variant="body2"><strong>Type:</strong> {selectedMedia.mediaOrganizationType}</Typography>
                                    <Typography variant="body2"><strong>Role:</strong> {selectedMedia.role}</Typography>
                                    {selectedMedia.websiteUrl && (
                                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LanguageIcon color="action" fontSize="small" />
                                            <Typography variant="body2" component="a" href={selectedMedia.websiteUrl} target="_blank" rel="noreferrer" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                {selectedMedia.websiteUrl}
                                            </Typography>
                                        </Box>
                                    )}

                                    {selectedMedia.socialMedia && selectedMedia.socialMedia.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ShareIcon fontSize="small" /> Social Presence
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {selectedMedia.socialMedia.map((social, index) => {
                                                    const getSocialLink = (platform, handle) => {
                                                        if (handle.startsWith('http')) return handle;
                                                        const cleanHandle = handle.replace('@', '');
                                                        switch (platform.toLowerCase()) {
                                                            case 'instagram': return `https://instagram.com/${cleanHandle}`;
                                                            case 'twitter (x)': return `https://twitter.com/${cleanHandle}`;
                                                            case 'facebook': return `https://facebook.com/${cleanHandle}`;
                                                            case 'linkedin': return `https://linkedin.com/in/${cleanHandle}`;
                                                            case 'tiktok': return `https://tiktok.com/@${cleanHandle}`;
                                                            case 'youtube': return `https://youtube.com/@${cleanHandle}`;
                                                            default: return null;
                                                        }
                                                    };

                                                    const link = getSocialLink(social.platform, social.handle);

                                                    return (
                                                        <Grid item xs={12} sm={6} key={index}>
                                                            <Paper
                                                                variant="outlined"
                                                                component={link ? 'a' : 'div'}
                                                                href={link}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                sx={{
                                                                    p: 1.5,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                    textDecoration: 'none',
                                                                    color: 'inherit',
                                                                    transition: 'all 0.2s',
                                                                    cursor: link ? 'pointer' : 'default',
                                                                    '&:hover': link ? {
                                                                        borderColor: 'primary.main',
                                                                        bgcolor: 'action.hover',
                                                                        transform: 'translateY(-2px)'
                                                                    } : {}
                                                                }}
                                                            >
                                                                <Box sx={{ color: 'primary.main' }}>
                                                                    {social.platform.toLowerCase().includes('instagram') && <InstagramIcon />}
                                                                    {social.platform.toLowerCase().includes('twitter') && <TwitterIcon />}
                                                                    {social.platform.toLowerCase().includes('facebook') && <FacebookIcon />}
                                                                    {social.platform.toLowerCase().includes('linkedin') && <LinkedInIcon />}
                                                                    {social.platform.toLowerCase().includes('youtube') && <YouTubeIcon />}
                                                                    {/* Fallback icon for others/tiktok if icon not available */}
                                                                    {!['instagram', 'twitter', 'facebook', 'linkedin', 'youtube'].some(p => social.platform.toLowerCase().includes(p)) && <LinkIcon />}
                                                                </Box>
                                                                <Box sx={{ overflow: 'hidden' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>{social.platform}</Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                        {social.handle}
                                                                    </Typography>
                                                                </Box>
                                                                {link && <OpenInNewIcon sx={{ ml: 'auto', fontSize: 16, color: 'text.disabled' }} />}
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Box>
                                    )}

                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>Status</Typography>
                                    <Chip label={selectedMedia.status} color={getStatusColor(selectedMedia.status)} />
                                    {selectedMedia.rejectionReason && (
                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}><strong>Reason:</strong> {selectedMedia.rejectionReason}</Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Identification</Typography>
                                    {selectedMedia.meansOfIdentification && (
                                        <Box
                                            component="img"
                                            src={getImageUrl(selectedMedia.meansOfIdentification)}
                                            alt="ID"
                                            sx={{
                                                width: '100%',
                                                maxHeight: 300,
                                                objectFit: 'contain',
                                                border: '1px solid #eee',
                                                borderRadius: 1
                                            }}
                                        />
                                    )}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ mt: 1 }}
                                        onClick={() => window.open(getImageUrl(selectedMedia.meansOfIdentification), '_blank')}
                                    >
                                        Open in New Tab
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Close</Button>
                            {selectedMedia.status === 'pending' && (
                                <>
                                    <Button color="error" onClick={() => handleReject(selectedMedia)}>Reject</Button>
                                    <Button variant="contained" color="success" onClick={() => handleVerify(selectedMedia)}>Verify</Button>
                                </>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Verification/Rejection Action Dialog */}
            <Dialog open={verificationDialog.open} onClose={() => setVerificationDialog({ ...verificationDialog, open: false })}>
                <DialogTitle>{verificationDialog.type === 'verify' ? 'Approve Registration' : 'Reject Registration'}</DialogTitle>
                <DialogContent>
                    {verificationDialog.type === 'verify' ? (
                        <Typography>Are you sure you want to verify this media personnel?</Typography>
                    ) : (
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Rejection Reason"
                            fullWidth
                            value={verificationDialog.reason}
                            onChange={(e) => setVerificationDialog({ ...verificationDialog, reason: e.target.value })}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVerificationDialog({ ...verificationDialog, open: false })}>Cancel</Button>
                    <Button
                        onClick={handleVerificationSubmit}
                        variant="contained"
                        color={verificationDialog.type === 'verify' ? 'success' : 'error'}
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : (verificationDialog.type === 'verify' ? 'Verify' : 'Reject')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MediaManagement;
